import { dehydrate, QueryClient } from '@tanstack/react-query';
import {
  BOOKING_CHANNEL,
  GET_SEO_INFORMATION,
  HOTEL_AVAILABILITY_QUERY,
  PageName,
  SITE_LEISURE,
  STATIC_HOTEL_INFORMATION_QUERY,
  PROMO_CODE_COOKIE,
  DISCOUNT_RATE_INFORMATION_QUERY,
  HIRoomRate,
  SEARCH_COMPANY_BY_ID,
  HOTEL_AVAILABILITY_DISCOUNT_RATE_QUERY,
  FT_PI_RATE_CARD_ORDERING,
  FT_PI_PROMO_CODE_LANDING_PAGE,
  GET_GLOBAL_CONFIG_QUERY,
  getStaticContent,
  FT_PI_BB_CCUI_BARRIER_FREE_LABEL,
  CountryCode,
  GET_PROMO_INFORMATION,
} from '@whitbread-eos/api';
import {
  axiosRequest,
  decodeIdToken,
  ID_TOKEN_COOKIE,
  getAvailabilityParamsFromUrl,
  getGQLClient,
  getHotelAvailabilityQueryKey,
  graphQLRequest,
  QueriesLogger,
  logger,
} from '@whitbread-eos/utils';
import Cookies from 'cookies';
import { GetServerSidePropsContext } from 'next';

interface Props extends GetServerSidePropsContext {
  queryClient: QueryClient;
  language: string;
  country: string;
  featureToggles: { [key: string]: boolean };
}

const createHDPPiDataLoaderFn = async ({
  queryClient,
  language,
  country,
  featureToggles,
  params,
  req,
  res,
  query,
}: Props) => {
  const client = getGQLClient();

  const cookies = new Cookies(req, res);
  const idTokenCookie = cookies.get(ID_TOKEN_COOKIE);

  const { prefetchQuery, fetchQuery, logQueries } = new QueriesLogger(
    queryClient,
    req,
    res,
    query,
    'PI | HDP | Hotel Details Page'
  );

  const {
    [FT_PI_RATE_CARD_ORDERING]: isRateCardOrderingEnabled,
    [FT_PI_PROMO_CODE_LANDING_PAGE]: isPromoCodeLandingPageEnabled,
    [FT_PI_BB_CCUI_BARRIER_FREE_LABEL]: isBarrierFreeLabelEnabledFT,
  } = featureToggles;
  const isBarrierFreeLabelEnabled =
    language === CountryCode.DE ? isBarrierFreeLabelEnabledFT : false;

  const slugItems = params?.slug as string[];
  const slug = ['/hotels', ...slugItems].join('/');

  const staticHotelData = await fetchQuery(
    ['staticHotelInformation', language, country, slug],
    () =>
      graphQLRequest(
        STATIC_HOTEL_INFORMATION_QUERY,
        {
          slug,
          language,
          country,
        },
        undefined,
        undefined,
        client
      )
  );

  /* get necessary data to prefetch availability */
  const { hotelId, brand } = staticHotelData.hotelInformationBySlug;
  const { arrival, departure, rooms, empOfferCodes, numberOfNights, corpId, promoId } =
    getAvailabilityParamsFromUrl(req.url!.split('?')[1]);

  const landingPagePromoCode = isPromoCodeLandingPageEnabled && promoId ? promoId : '';

  // prefetching hotelAvailability
  let hotelAvailabilityQuery;
  if (numberOfNights !== 0 && !corpId && !promoId) {
    hotelAvailabilityQuery = fetchQuery(
      getHotelAvailabilityQueryKey(
        language,
        country,
        brand,
        hotelId,
        arrival,
        departure,
        rooms,
        empOfferCodes,
        landingPagePromoCode
      ),
      () =>
        graphQLRequest(
          HOTEL_AVAILABILITY_QUERY,
          {
            hotelId,
            arrival,
            departure,
            rooms,
            brand: brand.toLowerCase(),
            language,
            country,
            bookingChannel: {
              channel: BOOKING_CHANNEL.PI,
              language: language?.toUpperCase(),
              subchannel: 'WEB',
            },
            channel: BOOKING_CHANNEL.PI,
            ratePlanCodes: empOfferCodes,
            promotionCode: landingPagePromoCode || cookies.get(PROMO_CODE_COOKIE),
          },
          undefined,
          undefined,
          client
        )
    );
  }

  let ratesInformationQuery;
  if (numberOfNights !== 0 && corpId) {
    let searchCompanyByIdResult;
    try {
      searchCompanyByIdResult = await fetchQuery(['searchCompanyById', corpId], () =>
        graphQLRequest(SEARCH_COMPANY_BY_ID, {
          corpId,
        })
      );
    } catch (error) {
      logger.error(error);
    }
    const { companyId } = searchCompanyByIdResult?.companyProfile ?? {};

    const hotelAvailabilityResult = await fetchQuery(
      [
        'hotelAvailabilityDiscountRate',
        language,
        country,
        brand.toLowerCase(),
        hotelId,
        companyId,
        arrival,
        departure,
        JSON.stringify(rooms),
        BOOKING_CHANNEL.PI,
      ].filter(Boolean),
      () =>
        graphQLRequest(HOTEL_AVAILABILITY_DISCOUNT_RATE_QUERY, {
          hotelId,
          arrival,
          departure,
          rooms,
          brand: brand.toLowerCase(),
          language,
          country,
          companyId,
          bookingChannel: {
            channel: BOOKING_CHANNEL.PI,
            language: language?.toUpperCase(),
            subchannel: 'WEB',
          },
          channel: BOOKING_CHANNEL.PI,
        })
    );

    const ratePlanCodes = hotelAvailabilityResult?.hotelAvailability?.roomRates.map(
      (roomRate: HIRoomRate) => roomRate.ratePlanCode
    );

    ratesInformationQuery = prefetchQuery(
      [
        'ratesInformationDiscountRate',
        language,
        country,
        brand.toLowerCase(),
        hotelId,
        BOOKING_CHANNEL.PI,
        ratePlanCodes,
      ],
      () =>
        graphQLRequest(DISCOUNT_RATE_INFORMATION_QUERY, {
          hotelId,
          brand: brand.toLowerCase(),
          language,
          country,
          channel: BOOKING_CHANNEL.PI,
          ratePlans: ratePlanCodes?.toString(),
        })
    );
  }

  const getGlobalConfigQuery = prefetchQuery(
    ['getGlobalConfig', brand, BOOKING_CHANNEL.PI, country, language],
    () =>
      graphQLRequest(GET_GLOBAL_CONFIG_QUERY, {
        brand,
        channel: BOOKING_CHANNEL.PI,
        country,
        language,
      }),
    { enabled: !!isRateCardOrderingEnabled }
  );
  const staticContentQuery = getStaticContent(isBarrierFreeLabelEnabled);

  const getStaticContentQuery = fetchQuery(['GetStaticContent', language, country], () =>
    graphQLRequest(
      staticContentQuery,
      {
        language,
        country,
        site: SITE_LEISURE,
        businessBooker: false,
      },
      undefined,
      undefined,
      client
    )
  );

  const seoQuery = prefetchQuery(['seoInformation', language, country, PageName.HDP, hotelId], () =>
    graphQLRequest(
      GET_SEO_INFORMATION,
      {
        language,
        country,
        page: PageName.HDP,
        hotelId,
      },
      undefined,
      undefined,
      client
    )
  );

  const promises = [getStaticContentQuery, seoQuery, hotelAvailabilityQuery];
  if (corpId) {
    promises.push(ratesInformationQuery);
  }

  if (isPromoCodeLandingPageEnabled && query?.PROMOID) {
    const promoId = query?.PROMOID || null;
    const promoInformationQuery = prefetchQuery(
      [
        'promotionsInformation',
        country,
        language,
        BOOKING_CHANNEL.PI,
        brand as string,
        promoId as string,
        '',
        arrival,
        departure,
      ],
      () =>
        graphQLRequest(
          GET_PROMO_INFORMATION,
          {
            country,
            language: language,
            channel: BOOKING_CHANNEL.PI,
            brand: brand as string,
            promotionCode: promoId as string,
            bookingDate: '',
            stayStartDate: arrival,
            stayEndDate: departure,
          },
          undefined,
          undefined,
          client
        )
    );
    promises.push(promoInformationQuery);
  }

  if (isRateCardOrderingEnabled) {
    promises.push(getGlobalConfigQuery);
  }

  if (idTokenCookie) {
    const { email } = decodeIdToken(idTokenCookie) || {};

    const axiosProps = {
      method: 'GET',
      url: `${process.env.NEXT_PUBLIC_ACCOUNT_SERVICE}/customers/hotels/${email}?business=false`,
      headers: {
        Authorization: `Bearer ${idTokenCookie}`,
      },
    };

    const userDetailsQuery = prefetchQuery(['userDetails', idTokenCookie], () =>
      axiosRequest(axiosProps)
    );
    promises.push(userDetailsQuery);
  }

  await Promise.all(promises);

  logQueries(performance.now());

  return {
    dehydratedState: dehydrate(queryClient),
  };
};

export default createHDPPiDataLoaderFn;
